import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateAutomationTemplateDraftStateQuery } from '../../queries/useAutomationTemplateHasDraftQuery';
import {
	AUTOMATION_QUERY_KEYS,
	AUTOMATION_STEP_MUTATION_SCOPE,
	AUTOMATION_TEMPLATE_MUTATION_KEY,
} from '../../queryKeys';

interface Params {
	templateId: string;
	stepType: Api.AutomationStepType;
	stepId: string;
	impersonationContext?: Api.IImpersonationContext;
	relatedTemplateIds?: string[];
}

export function useAutomationTemplateRemoveStepMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate, variables: Params) => void;
	onError?: (error: Api.IOperationResultNoValue, variables: Params) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		scope: AUTOMATION_STEP_MUTATION_SCOPE,
		mutationFn: ({ templateId, stepType, stepId, impersonationContext }: Params) => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}/Step/${encodeURIComponent(stepType)}/${encodeURIComponent(stepId)}`,
					impersonationContext,
				}),
				'DELETE',
				null
			);
		},
		onSuccess: (data, variables) => {
			const templateIdsToInvalidate = new Set([variables.templateId, ...(variables.relatedTemplateIds ?? [])]);
			for (const templateId of templateIdsToInvalidate) {
				queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
				invalidateAutomationTemplateDraftStateQuery(templateId);
			}
			onSuccess?.(data, variables);
		},
		onError,
	});
}
